import React, { useCallback, useEffect, useMemo, useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../styles/styles.css';
import '../styles/formularios.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Swal from 'sweetalert2';

import logoInstituto from '../components/images/logo.png';
import logoSEP from '../components/images/SEP_Logo_2019.svg.png';
import { resolveApiBaseList } from '../utils/apiBase';


// Días para las columnas
const DIAS = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes'];

// Ventana y recesos (para construir filas dinámicas)
const WINDOW_START = '07:00';
const WINDOW_END   = '15:00';
const RECESOS = [
  ['10:00', '10:30'],
  ['12:30', '12:45'],
];

// helpers de tiempo
const hhmm = (t) => (t || '').slice(0, 5); // 'HH:MM:SS' -> 'HH:MM'
const overlaps = (aIni, aFin, bIni, bFin) => (aIni < bFin) && (aFin > bIni);

// Construye filas (renglones) dinámicos a partir de la ventana, recesos y las clases
function buildFilas(horarios) {
  const cuts = new Set([WINDOW_START, WINDOW_END]);
  // cortes por recesos
  for (const [ri, rf] of RECESOS) { cuts.add(ri); cuts.add(rf); }
  // cortes por clases (usa HH:MM)
  (horarios || []).forEach(h => { cuts.add(hhmm(h.horaInicio)); cuts.add(hhmm(h.horaFin)); });

  const sorted = Array.from(cuts).sort(); // 'HH:MM' ⇒ orden lexicográfico es válido
  const filas = [];
  for (let i = 0; i < sorted.length - 1; i++) {
    const ini = sorted[i], fin = sorted[i + 1];
    if (ini >= fin) continue;

    const esReceso = RECESOS.some(([ri, rf]) => overlaps(ini, fin, ri, rf));
    filas.push({ ini, fin, label: `${ini} - ${fin}`, receso: esReceso });
  }
  return filas;
}

// helper para transformar imagen a dataURL (para jsPDF.addImage)
async function imgToDataURL(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = reject;
    img.src = src;
  });
}

const ConsultarHorariosDocente = () => {
  const [mostrarResultado, setMostrarResultado] = useState(false);
  const [nombreDocente, setNombreDocente] = useState('');
  const [horarios, setHorarios] = useState([]); // [{dia, horaInicio, horaFin, aula, materia}]

  // grupos del docente y grupo elegido
  const [grupos, setGrupos] = useState([]);
  const [grupoId, setGrupoId] = useState('');

  const apiBases = useMemo(() => resolveApiBaseList(), []);

  const fetchJsonWithFallback = useCallback(async (path, options = {}) => {
    let lastError = null;
    for (const base of apiBases) {
      const normalizedBase = base.replace(/\/$/, '');
      const normalizedPath = path.startsWith('/') ? path : `/${path}`;
      const url = `${normalizedBase}${normalizedPath}`;
      try {
        const response = await fetch(url, options);
        const text = await response.text();

        if (!response.ok) {
          let message = text || `Error ${response.status}`;
          try {
            const parsed = text ? JSON.parse(text) : null;
            if (parsed && typeof parsed === 'object') {
              message = parsed.message || parsed.error || message;
            }
          } catch (parseErr) {
            // mantener mensaje original
          }
          const error = new Error(message);
          error.status = response.status;
          lastError = error;
          console.warn(`Warning: petición fallida a ${url}:`, error);
          continue;
        }

        const contentType = response.headers.get('content-type') || '';
        const trimmed = text.trim();
        if (!trimmed) return {};

        if (contentType.includes('application/json') || trimmed.startsWith('{') || trimmed.startsWith('[')) {
          return JSON.parse(trimmed);
        }

        try {
          return JSON.parse(trimmed);
        } catch (parseErr) {
          lastError = new Error(`Respuesta inválida desde ${url}`);
          console.warn(`Warning: contenido no JSON desde ${url} (content-type: ${contentType || 'desconocido'})`);
        }
      } catch (error) {
        lastError = error;
        console.warn(`Warning: error de red al solicitar ${url}:`, error);
      }
    }

    throw lastError || new Error('No se pudo contactar al servidor.');
  }, [apiBases]);

  // Al montar: tomar el displayName del login y cargar sus grupos
  useEffect(() => {
    const displayName = localStorage.getItem('displayName') || 'Usuario';
    setNombreDocente(displayName);

    const cargarGrupos = async () => {
      try {
        const data = await fetchJsonWithFallback(`/horarios/mis-grupos?docenteNombre=${encodeURIComponent(displayName)}`);
        setGrupos(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('No se pudieron cargar los grupos del docente:', error);
        setGrupos([]);
      }
    };

    cargarGrupos();
  }, [fetchJsonWithFallback]);

  const consultarHorario = useCallback(async () => {
    if (!grupoId) {
      return Swal.fire('Atención', 'Por favor, selecciona un grupo.', 'warning');
    }
    const docenteTexto = nombreDocente;
    try {
      const data = await fetchJsonWithFallback(
        `/horarios/mis-horarios?docenteNombre=${encodeURIComponent(docenteTexto)}&grupoId=${encodeURIComponent(grupoId)}`
      );

      const adaptado = (data || []).map(row => ({
        dia: row.DiaSemana,
        horaInicio: hhmm(row.HoraInicio),
        horaFin: hhmm(row.HoraFin),
        aula: row.Aula ?? '',
        materia: row.Materia ?? ''
      }));

      setHorarios(adaptado);
      setMostrarResultado(true);

      const grupoSel = grupos.find(g => String(g.IdGrupo) === String(grupoId));
      if (adaptado.length === 0) {
        await Swal.fire({
          icon: 'warning',
          title: 'Sin horarios asignados',
          html: `
            <div class="text-start">
              <div><b>Docente:</b> ${docenteTexto}</div>
              <div><b>Grupo:</b> ${grupoId}${grupoSel ? ` — Grado ${grupoSel.Grado}` : ''}</div>
            </div>
          `
        });
        return;
      }

      await Swal.fire({
        icon: 'success',
        title: 'Horario cargado',
        html: `
          <div class="text-start">
            <div><b>Docente:</b> ${docenteTexto}</div>
            <div><b>Grupo:</b> ${grupoId}${grupoSel ? ` — Grado ${grupoSel.Grado}` : ''}</div>
            <div><b>Bloques registrados:</b> ${adaptado.length}</div>
          </div>
        `,
        showConfirmButton: false,
        timer: 2000
      });
    } catch (err) {
      console.error(err);
      setHorarios([]);
      setMostrarResultado(true);
      Swal.fire('Error', `No fue posible obtener el horario.<br>${err.message || ''}`, 'error');
    }
  }, [fetchJsonWithFallback, grupoId, grupos, nombreDocente]);

  // Filas dinámicas de la tabla (si no hay horarios, usa solo ventana+recesos)
  const FILAS = useMemo(() => buildFilas(horarios), [horarios]);

  const exportarExcel = () => {
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.table_to_sheet(document.getElementById('tablaHorarios'));
    XLSX.utils.book_append_sheet(wb, ws, 'Horarios');
    XLSX.writeFile(wb, 'horarios.xlsx');
  };

  // Exportar PDF usando las mismas filas dinámicas
  const exportarPDF = async () => {
    const doc = new jsPDF('p', 'pt');
    const margenX = 40;

    try {
      const imgLeft = await imgToDataURL(logoInstituto);
      doc.addImage(imgLeft, 'PNG', margenX, 28, 52, 52);
    } catch {}

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text('Instituto Silviano Carrillo', doc.internal.pageSize.getWidth() / 2, 50, { align: 'center' });

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.text('Consulta de Horarios del Docente', doc.internal.pageSize.getWidth() / 2, 68, { align: 'center' });

    const cajaTop = 90;
    const cajaAlt = 70;
    doc.setDrawColor(230);
    doc.setFillColor(247, 247, 247);
    doc.roundedRect(margenX, cajaTop, doc.internal.pageSize.getWidth() - margenX * 2, cajaAlt, 6, 6, 'FD');

    const claveEscuela = '14PR0155G';
    const cicloEscolar = 'AGOSTO-JULIO 2025-2026';
    const leyendaHorario = 'Ventana 07:00–15:00';

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text(`MAESTRO: ${nombreDocente}`, margenX + 14, cajaTop + 26);
    doc.text(`CLAVE: ${claveEscuela}`, margenX + 14, cajaTop + 46);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.text('HORARIO DE CLASES', margenX + 14, cajaTop + 64);
    doc.text(cicloEscolar, margenX + 170, cajaTop + 64);
    doc.text(leyendaHorario, doc.internal.pageSize.getWidth() / 2, cajaTop + cajaAlt + 22, { align: 'center' });

    const head = [['Hora', ...DIAS]];
    const body = FILAS.map(f => {
      if (f.receso) {
        return [
          { content: f.label, styles: { fontStyle: 'bold' } },
          { content: 'RECESO', colSpan: DIAS.length, styles: { halign: 'center', fontStyle: 'bold', fillColor: [240,240,240] } }
        ];
      }
      const fila = [f.label];
      DIAS.forEach(d => {
        const clase = horarios.find(h => h.dia === d && overlaps(f.ini, f.fin, h.horaInicio, h.horaFin));
        fila.push(clase ? `${clase.materia || ''}${clase.aula ? `\nAula ${clase.aula}` : ''}` : '');
      });
      return fila;
    });

    autoTable(doc, {
      head,
      body,
      startY: cajaTop + cajaAlt + 40,
      styles: { halign: 'center', valign: 'middle', fontSize: 9, cellPadding: 4 },
      headStyles: { fillColor: [230,230,230] },
      columnStyles: { 0: { cellWidth: 110, fontStyle: 'bold' } },
      theme: 'grid',
      margin: { left: margenX, right: margenX },
    });

    doc.save(`horario_${nombreDocente.replace(/\s+/g, '_')}.pdf`);
  };

  return (
    <div className="container py-4 px-4">
      {/* Encabezado */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <img src={logoInstituto} alt="Logo Instituto" className="logo" />
        <div className="flex-grow-1 text-center">
          <h4 className="mb-0">Instituto Silviano Carrillo</h4>
          <small className="text-muted fs-5">Consulta de Horario Personal</small>
        </div>
        <img src={logoSEP} alt="Logo SEP" className="logo" />
      </div>

      {/* Formulario */}
      <div className="p-4 bg-light rounded shadow-sm">
        <form className="row g-3">
          <div className="col-md-6">
            <label className="form-label">Docente</label>
            <input type="text" className="form-control" value={nombreDocente} readOnly />
          </div>

          <div className="col-md-6">
            <label htmlFor="grupo" className="form-label">Grupo</label>
            <select
              className="form-select"
              id="grupo"
              value={grupoId}
              onChange={(e) => setGrupoId(e.target.value)}
            >
              <option value="">Selecciona un grupo...</option>
              {grupos.map(g => (
                <option key={g.IdGrupo} value={g.IdGrupo}>
                  {`Grupo ${g.IdGrupo} — Grado ${g.Grado} (${g.CicloEscolar || ''})`}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-12">
            <button type="button" className="btn btn-primary w-100" onClick={consultarHorario}>
              Consultar Horario
            </button>
          </div>
        </form>
      </div>

      {/* Resultados */}
      {mostrarResultado && (
        <div id="resultadoConsulta" className="mt-4">
          <h5 className="text-center">
            Horario de clases de <span>{nombreDocente}</span>
          </h5>

          {/* Tabla SEMANAL con filas DINÁMICAS */}
          <table id="tablaHorarios" className="table table-bordered text-center align-middle">
            <thead>
              <tr>
                <th style={{ width: '140px' }}>Hora</th>
                {DIAS.map(d => <th key={d}>{d}</th>)}
              </tr>
            </thead>
            <tbody>
              {FILAS.map(f => (
                <tr key={f.label}>
                  <td className="fw-semibold">{f.receso ? `${f.label} (RECESO)` : f.label}</td>
                  {f.receso
                    ? <td colSpan={DIAS.length} className="table-secondary fw-bold">Receso</td>
                    : DIAS.map(d => {
                        const clase = horarios.find(h => h.dia === d && overlaps(f.ini, f.fin, h.horaInicio, h.horaFin));
                        return (
                          <td key={`${f.label}-${d}`}>
                            {clase ? (
                              <>
                                <div className="fw-semibold">{clase.materia || '—'}</div>
                                {clase.aula ? <small className="text-muted">Aula {clase.aula}</small> : null}
                              </>
                            ) : null}
                          </td>
                        );
                      })
                  }
                </tr>
              ))}
            </tbody>
          </table>

          {/* Botones de exportación */}
          <div className="mt-3 d-flex justify-content-end gap-2">
            <button className="btn btn-outline-success" onClick={exportarExcel}>Exportar a Excel</button>
            <button className="btn btn-outline-danger" onClick={exportarPDF}>Exportar a PDF</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConsultarHorariosDocente;

